import React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  // VisibilityState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import MagnifierIcon from '../../assets/icons/magnifier.svg';

const VIRTUALIZED_ROW_HEIGHT = 80;
const VIRTUALIZED_ROW_OVERSCAN = 6;
const VIRTUALIZED_ROW_THRESHOLD = 30;
const DEFAULT_COLUMN_WIDTH = 150;
const DATA_TABLE_COLUMN_WIDTHS: Record<string, number> = {
  client: 360,
  lastViewedAt: 120,
  status: 150,
  notebook: 90,
  noOfItems: 115,
  estimate: 280,
  invoice: 190,
  installDate: 130,
  actions: 64,
};

const getColumnWidth = (columnId: string) => {
  if (columnId.startsWith('assignment_role_')) {
    return 150;
  }

  if (columnId.startsWith('customColumn')) {
    return 170;
  }

  return DATA_TABLE_COLUMN_WIDTHS[columnId] || DEFAULT_COLUMN_WIDTH;
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  handleResetAllFilters(): void;
  isFiltersActivate: boolean;
  columnVisibility: any;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  handleResetAllFilters,
  isFiltersActivate,
  columnVisibility,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  // const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const measuredRowHeightsRef = React.useRef<Record<number, number>>({});
  const pendingScrollMetricsRef = React.useRef({ scrollTop: 0, clientHeight: 0 });
  const scrollFrameRef = React.useRef<number | null>(null);
  const [scrollMetrics, setScrollMetrics] = React.useState({ scrollTop: 0, clientHeight: 0 });
  const [estimatedRowHeight, setEstimatedRowHeight] = React.useState(VIRTUALIZED_ROW_HEIGHT);

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: 0,
        pageSize: data.length || 0,
      },
    },
  });

  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const visibleColumnCount = Math.max(visibleColumns.length, 1);
  const tableMinWidth = visibleColumns.reduce((width, column) => {
    return width + getColumnWidth(column.id);
  }, 0);
  const shouldVirtualizeRows = rows.length > VIRTUALIZED_ROW_THRESHOLD;
  const viewportHeight =
    scrollMetrics.clientHeight || wrapperRef.current?.clientHeight || VIRTUALIZED_ROW_HEIGHT * 8;
  const virtualStartIndex = shouldVirtualizeRows
    ? Math.max(
        0,
        Math.floor(scrollMetrics.scrollTop / estimatedRowHeight) - VIRTUALIZED_ROW_OVERSCAN
      )
    : 0;
  const virtualRowCount = shouldVirtualizeRows
    ? Math.ceil(viewportHeight / estimatedRowHeight) + VIRTUALIZED_ROW_OVERSCAN * 2
    : rows.length;
  const virtualEndIndex = shouldVirtualizeRows
    ? Math.min(rows.length, virtualStartIndex + virtualRowCount)
    : rows.length;
  const virtualRows = shouldVirtualizeRows
    ? rows.slice(virtualStartIndex, virtualEndIndex)
    : rows;
  const topSpacerHeight = shouldVirtualizeRows ? virtualStartIndex * estimatedRowHeight : 0;
  const bottomSpacerHeight = shouldVirtualizeRows
    ? Math.max(0, rows.length - virtualEndIndex) * estimatedRowHeight
    : 0;

  const handleWrapperScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight } = event.currentTarget;
    pendingScrollMetricsRef.current = { scrollTop, clientHeight };

    if (scrollFrameRef.current !== null) return;

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      setScrollMetrics((current) => {
        const next = pendingScrollMetricsRef.current;
        return current.scrollTop === next.scrollTop && current.clientHeight === next.clientHeight
          ? current
          : next;
      });
    });
  }, []);

  const measureRow = React.useCallback((index: number, node: HTMLTableRowElement | null) => {
    if (!node) return;
    const height = node.getBoundingClientRect().height;
    if (!height) return;

    measuredRowHeightsRef.current[index] = height;
    const measuredHeights = Object.values(measuredRowHeightsRef.current);
    if (!measuredHeights.length) return;

    const averageHeight =
      measuredHeights.reduce((sum, measuredHeight) => sum + measuredHeight, 0) /
      measuredHeights.length;
    setEstimatedRowHeight((currentHeight) =>
      Math.abs(currentHeight - averageHeight) > 2 ? Math.round(averageHeight) : currentHeight
    );
  }, []);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    setScrollMetrics((current) => {
      const next = {
        scrollTop: wrapper.scrollTop,
        clientHeight: wrapper.clientHeight,
      };

      return current.scrollTop === next.scrollTop && current.clientHeight === next.clientHeight
        ? current
        : next;
    });
  }, [rows.length, columnVisibility]);

  React.useEffect(() => {
    return () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  return (
    <Table
      showTopScrollbar
      wrapperRef={wrapperRef}
      onWrapperScroll={handleWrapperScroll}
      className="table-fixed"
      style={{ minWidth: tableMinWidth || undefined }}
    >
      <colgroup>
        {visibleColumns.map((column) => (
          <col key={column.id} style={{ width: getColumnWidth(column.id) }} />
        ))}
      </colgroup>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {rows?.length ? (
          <>
            {topSpacerHeight > 0 && (
              <TableRow aria-hidden="true" className="border-0 hover:bg-transparent">
                <TableCell
                  colSpan={visibleColumnCount}
                  className="!static !table-cell !border-0 !p-0"
                  style={{ height: topSpacerHeight, position: 'static' }}
                />
              </TableRow>
            )}

            {virtualRows.map((row) => (
              <TableRow
                key={row.id}
                ref={(node) => measureRow(row.index, node)}
                data-state={row.getIsSelected() && 'selected'}
                className={
                  (row.original as any)?.archived
                    ? '!bg-[#f3f4f6] hover:!bg-[#eceff3] [&>td]:!bg-[#f3f4f6] hover:[&>td]:!bg-[#eceff3]'
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {bottomSpacerHeight > 0 && (
              <TableRow aria-hidden="true" className="border-0 hover:bg-transparent">
                <TableCell
                  colSpan={visibleColumnCount}
                  className="!static !table-cell !border-0 !p-0"
                  style={{ height: bottomSpacerHeight, position: 'static' }}
                />
              </TableRow>
            )}
          </>
        ) : (
          <TableRow className="hover:bg-white">
            <TableCell
              colSpan={visibleColumnCount}
              className="text-center w-full max-w-none static h-full !table-cell"
            >
              <div
                style={{ gap: '16px' }}
                className="h-full flex items-center justify-center flex-col"
              >
                {isFiltersActivate && (
                  <div>
                    <MagnifierIcon />
                  </div>
                )}

                <span className="text-xl">No results</span>

                {isFiltersActivate && (
                  <div>
                    <button
                      style={{ fontSize: '16px' }}
                      className="clear_filter_btn"
                      type="button"
                      onClick={() => handleResetAllFilters()}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
